import { useState } from "react";

import {
  Edit,
  Key,
  KeyRound,
  Mail,
  Monitor,
  MoreHorizontal,
  Package,
  Scan,
  Server,
  Shield,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isCenterLike } from "@/features/servers/utils/ficha";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { UserActionsBottomSheet } from "@/features/users/components/UserActionsBottomSheet";
import type { UserAction } from "@/features/users/components/UserActionsDialog";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName, getUserInitials } from "@/features/users/utils/user-utils";
import { BREAKPOINTS, useMediaQuery } from "@/shared/hooks/useMediaQuery";

import { UserInstallationsDialog } from "./UserInstallationsDialog";

interface UserCardProps {
  user: User;
  currentUser?: User | null;
  onEdit: (user: User) => void;
  onView: (user: User) => void;
  onAction: (user: User, action: UserAction) => void;
  onViewImagings?: (user: User) => void;
  /** Show reserved slot for future Imagenología integration */
  showImagingBadge?: boolean;
}

const ROLE_MAP: Record<string, string> = {
  admin: "Admin",
  physician: "Médico",
  patient: "Paciente",
  institution: "Institución",
};

/**
 * Stacked Card component for mobile user list.
 * Displays essential info (name, role, status) with action menu.
 * Includes a reserved slot for Imagenología badge (future integration).
 */
export function UserCard({
  user,
  currentUser,
  onEdit,
  onView,
  onAction,
  onViewImagings,
  showImagingBadge = false,
}: UserCardProps) {
  const isCurrentUser = user.uid === currentUser?.uid;
  const isActive = user.auth ? user.auth.active : user.active;
  const roleName = ROLE_MAP[user.type] || "Usuario";
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const isTablet = useMediaQuery(BREAKPOINTS.tablet);
  const useBottomSheet = isMobile || isTablet;
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [showInstallations, setShowInstallations] = useState(false);
  const navigate = useNavigate();

  const { can } = usePermissions();
  // Capacidad e idoneidad del dato son cosas distintas: solo instituciones y
  // médicos tienen instalación y ficha de infraestructura.
  const canDownloadInstallations =
    isCenterLike(user) && can("installations:download_package");
  const canViewServers = can("servers:view") && isCenterLike(user);

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer active:bg-gray-50 dark:active:bg-gray-700/50"
      onClick={() => onView(user)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView(user);
        }
      }}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-sm font-semibold text-primary">
            {getUserInitials(user)}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {getUserFullName(user)}
            </p>
            {isCurrentUser && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                Tú
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</p>

          {/* Badges row */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <Badge variant="secondary" className="text-[10px]">
              {roleName}
            </Badge>
            <Badge variant={isActive ? "default" : "destructive"} className="text-[10px]">
              {isActive ? "Activo" : "Inactivo"}
            </Badge>
            {/* Reserved slot for Imagenología — visually prepared for future use */}
            {showImagingBadge && (
              <Badge
                variant="outline"
                className="text-[10px] border-teal-300 text-teal-600 dark:border-teal-700 dark:text-teal-400"
              >
                <Scan className="h-2.5 w-2.5 mr-0.5" />
                Img
              </Badge>
            )}
          </div>
        </div>

        {/* Actions menu */}
        <div
          className="shrink-0 flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Acceso a Imagenología: Requiere permiso de imaging, admin o support */}
          {onViewImagings && can("imagings:list") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewImagings(user);
                }}
                className="text-teal-600 hover:text-teal-700 hover:bg-teal-600/10 p-2 rounded-lg transition-colors tap-target"
                title="Ver Imagenología"
                aria-label="Ver Imagenología"
              >
                <Monitor className="h-5 w-5" />
              </button>
            )}

          {canViewServers && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/servers/${user.uid}`);
              }}
              className="text-sky-600 hover:text-sky-700 hover:bg-sky-600/10 p-2 rounded-lg transition-colors tap-target"
              title="Infraestructura / Servidores"
              aria-label="Infraestructura / Servidores"
            >
              <Server className="h-5 w-5" />
            </button>
          )}

          {/* Edición: Requiere admin, support o users */}
          {can("users:edit") && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(user);
              }}
              disabled={isCurrentUser}
              className="text-primary hover:text-primary/80 hover:bg-primary/10 p-2 rounded-lg transition-colors tap-target disabled:opacity-40 disabled:cursor-not-allowed"
              title="Editar usuario"
              aria-label="Editar usuario"
            >
              <Edit className="h-5 w-5" />
            </button>
          )}

          {useBottomSheet ? (
            <>
              <button
                className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted tap-target"
                aria-label="Acciones de usuario"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsActionsOpen(true);
                }}
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
              <UserActionsBottomSheet
                user={user}
                currentUser={currentUser}
                isOpen={isActionsOpen}
                onClose={() => setIsActionsOpen(false)}
                onEdit={onEdit}
                onAction={onAction}
                onViewImagings={onViewImagings}
              />
            </>
          ) : (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted tap-target"
                  aria-label="Acciones de usuario"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Gestión
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onEdit(user)} disabled={isCurrentUser}>
                  <Edit className="h-4 w-4 mr-2 text-primary" />
                  Editar perfil / email
                </DropdownMenuItem>
                {onViewImagings && can("imagings:list") && (
                    <DropdownMenuItem onClick={() => onViewImagings(user)}>
                      <Scan className="h-4 w-4 mr-2 text-teal-600" />
                      Imagenología
                    </DropdownMenuItem>
                  )}

                {canDownloadInstallations && (
                  <DropdownMenuItem onClick={() => setShowInstallations(true)}>
                    <Package className="h-4 w-4 mr-2 text-blue-500" />
                    Descargar Instalaciones
                  </DropdownMenuItem>
                )}

                {canViewServers && (
                  <DropdownMenuItem
                    onClick={() => navigate(`/servers/${user.uid}`)}
                  >
                    <Server className="h-4 w-4 mr-2 text-sky-600" />
                    Infraestructura / Servidores
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                {/* Acciones de Cuenta (Requiere admin, support o users) */}
                {can("users:toggle_active") && (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Contraseña
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => onAction(user, "reset-password-email")}
                    >
                      <Mail className="h-4 w-4 mr-2 text-amber-500" />
                      Enlace por correo
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onAction(user, "reset-password-direct")}
                    >
                      <Key className="h-4 w-4 mr-2 text-amber-500" />
                      Contraseña directa
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Cuenta
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onAction(user, "verify-email")}>
                      <Mail className="h-4 w-4 mr-2 text-green-500" />
                      Verificar por correo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAction(user, "verify-direct")}>
                      <ShieldCheck className="h-4 w-4 mr-2 text-green-500" />
                      Verificar directamente
                    </DropdownMenuItem>
                    {user.active ? (
                      <DropdownMenuItem
                        onClick={() => onAction(user, "disable")}
                        className="text-red-600 focus:text-red-600"
                        disabled={isCurrentUser}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Deshabilitar
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => onAction(user, "enable")}>
                        <Shield className="h-4 w-4 mr-2 text-blue-500" />
                        Habilitar
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Gestión Avanzada (Solo admin) */}
                {can("api_keys:list") && (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Avanzado
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onAction(user, "api-keys")}>
                      <KeyRound className="h-4 w-4 mr-2 text-purple-500" />
                      API Keys
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAction(user, "licenses")}>
                      <KeyRound className="h-4 w-4 mr-2 text-blue-500" />
                      Licencias
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {canDownloadInstallations && (
        <UserInstallationsDialog
          user={user}
          open={showInstallations}
          onClose={() => setShowInstallations(false)}
        />
      )}
    </div>
  );
}
